import { Lock, RefreshCw, Unlock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api, isApiError } from '@/api';
import type { LegalLock } from '@/api/areas/market';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { CopyText, EmptyState, Loading } from '@/components/States';
import { useRetained } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { EnumBadge, useEnumLabel, When } from '../../compliance/ui';
import { ABUSE_STATUS, DISPUTE_STATUS } from '../../compliance/vocab';
import { LOCK_TYPE } from '../shared';
import { SystemReason } from '@/components/SystemReason';

/** The case a lock was placed for, shown by its reference rather than an id. */
function PlacedFor({ lock }: { lock: LegalLock }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  if (lock.dispute) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <EntityRef
          label={`${label('disputeType', lock.dispute.dispute_type)} ${lock.dispute.case_reference}`}
          to={`/compliance/icann/disputes/${encodeURIComponent(lock.dispute.id)}`}
          id={lock.dispute.id}
          sub={lock.dispute.provider}
        />
        <EnumBadge group="disputeStatus" value={lock.dispute.status} tones={DISPUTE_STATUS} />
      </span>
    );
  }
  if (lock.abuse_case) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <EntityRef
          label={lock.abuse_case.case_number}
          to={`/compliance/icann/abuse/${encodeURIComponent(lock.abuse_case.id)}`}
          id={lock.abuse_case.id}
          sub={t('locks.domainAction', { action: label('domainAction', lock.abuse_case.action) })}
        />
        <EnumBadge group="abuseStatus" value={lock.abuse_case.status} tones={ABUSE_STATUS} />
      </span>
    );
  }
  return <span className="text-muted-foreground">{t('locks.noCase')}</span>;
}

/** `/compliance/legal-locks/:id`: one lock on a name, what it blocks and why it is there. */
export function LegalLockPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getLegalLock(id, { signal }), [id]);
  const data = useRetained(result.status === 'success' ? result.data : undefined);
  useDocumentTitle(data ? `${t('lockDetail.title')} ${data.lock.domain_name}` : t('lockDetail.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('locks.title'), to: '/compliance/legal-locks' },
    { label: data?.lock.domain_name ?? '…' },
  ];

  if (!data) {
    return (
      <>
        <PageHeader title={t('lockDetail.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError
            error={result.error}
            onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload}
            title={t('lockDetail.loadError')}
          />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const { lock, effects } = data;
  const blocked = effects ? (effects.blocks[lock.lock_type] ?? []) : null;

  const otherCols: Column<LegalLock>[] = [
    {
      key: 'lock',
      header: t('locks.col.lock'),
      cell: (l) => (
        <div className="flex flex-wrap gap-1">
          <EntityRef label={label('lockType', l.lock_type)} to={`/compliance/legal-locks/${encodeURIComponent(l.id)}`} id={l.id} />
          {l.active ? <Badge tone="pending">{t('locks.active')}</Badge> : <Badge tone="neutral">{t('locks.released')}</Badge>}
        </div>
      ),
    },
    { key: 'reason', header: t('locks.col.reason'), cell: (l) => <span className="text-sm"><SystemReason reason={l.reason} /></span> },
    { key: 'for', header: t('locks.col.placedFor'), cell: (l) => <PlacedFor lock={l} /> },
    { key: 'applied', header: t('locks.col.applied'), cell: (l) => <When at={l.applied_at} /> },
    { key: 'released', header: t('locks.col.released'), cell: (l) => (l.released_at ? <When at={l.released_at} /> : <span className="text-muted-foreground">—</span>) },
  ];

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {lock.active ? <Lock className="size-5" aria-hidden="true" /> : <Unlock className="size-5" aria-hidden="true" />}
            {label('lockType', lock.lock_type)}
          </span>
        }
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="lockType" value={lock.lock_type} tones={LOCK_TYPE} />
            {lock.active ? <Badge tone="warn">{t('locks.active')}</Badge> : <Badge tone="neutral">{t('locks.released')}</Badge>}
            <span aria-hidden="true">·</span>
            <EntityRef
              label={lock.unicode_name || lock.domain_name}
              to={`/domains/${encodeURIComponent(lock.domain_name)}`}
              id={lock.domain_id}
              sub={label('lifecycle', lock.lifecycle_state)}
            />
          </span>
        }
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-4">
        {effects === null ? (
          <Callout tone="crit" title={t('locks.noEffectsTitle')}>
            {t('lockDetail.noEffects')}
          </Callout>
        ) : null}
        {lock.active ? <Callout tone="info">{t('lockDetail.unlockHint')}</Callout> : null}

        <Section title={t('lockDetail.facts')}>
          <KeyValue
            columns={3}
            rows={[
              [t('lockDetail.domain'), <EntityRef key="d" label={lock.unicode_name || lock.domain_name} to={`/domains/${encodeURIComponent(lock.domain_name)}`} id={lock.domain_id} />],
              [t('lockDetail.type'), <EnumBadge key="lt" group="lockType" value={lock.lock_type} tones={LOCK_TYPE} />],
              [t('lockDetail.state'), lock.active ? t('locks.active') : t('locks.released')],
              [t('lockDetail.reason'), <SystemReason key="r" reason={lock.reason} />],
              [t('lockDetail.caseReference'), lock.case_reference ? <code key="cr">{lock.case_reference}</code> : null],
              [t('lockDetail.placedFor'), <PlacedFor key="pf" lock={lock} />],
              [t('lockDetail.applied'), formatDateTime(lock.applied_at)],
              [
                t('lockDetail.appliedBy'),
                <EntityRef key="ab" label={lock.applied_by_display_name ?? label('actorType', lock.applied_by_actor_type)} id={lock.applied_by_actor_id} />,
              ],
              lock.released_at ? [t('lockDetail.released'), formatDateTime(lock.released_at)] : false,
              lock.released_at
                ? [
                    t('lockDetail.releasedBy'),
                    <EntityRef key="rb" label={lock.released_by_display_name ?? label('actorType', lock.released_by_actor_type ?? '')} id={lock.released_by_actor_id} />,
                  ]
                : false,
              lock.release_reason ? [t('lockDetail.releaseReason'), <SystemReason key="rr" reason={lock.release_reason} />] : false,
              lock.approval_request_id
                ? [
                    t('lockDetail.approval'),
                    <Link key="ap" to="/admin/approvals" className="text-primary hover:underline">
                      {t('lockDetail.approval')}
                    </Link>,
                  ]
                : false,
              [t('lockDetail.id'), <CopyText key="id" value={lock.id} />],
            ]}
          />
        </Section>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title={t('lockDetail.blocks')}>
            {blocked === null ? (
              <p className="text-sm text-muted-foreground">{t('lockDetail.noEffects')}</p>
            ) : blocked.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('lockDetail.blocksNone')}</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {blocked.map((op) => (
                  <li key={op}>
                    <Badge tone="warn">{label('operation', op)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title={t('lockDetail.registry')}>
            {data.registry_statuses.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('lockDetail.noRegistryStatuses')}</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {data.registry_statuses.map((s) => (
                  <li key={s}>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{s}</code>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <Section title={t('lockDetail.other', { count: data.other_locks.length })} flush>
          <DataTable columns={otherCols} rows={data.other_locks} rowKey={(l) => l.id} empty={<EmptyState title={t('lockDetail.noOther')} />} />
        </Section>
      </div>
    </>
  );
}
